import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
// Modules
import { PartnerById, TParams } from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as partnerActions,
    selectors as partnerSelectors,
    types as partnerTypes,
} from '../../../redux/reducers/partners';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import { IBAN_INPUT_PATTERN, editIban, normalizeIban } from '../../../utils/businessUtils';
import ObjectUtils from '../../../utils/objectUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';

interface PartnerEditProps extends WithTranslation {
    match: match<TParams>;
    partners: PartnerById;
    editPartnerError: ErrorPayload;
    getPartner: (id: string) => void;
    editPartner: (id: string, payload: any) => void;
    isEditingPartner: boolean;
}

interface PartnerEditState {
    formData: any;
}

class PartnerEditPage extends React.Component<PartnerEditProps, PartnerEditState> {
    state = {
        formData: {},
    };

    componentDidMount() {
        const id = this.props.match.params.id;
        const partner = this.props.partners[id];
        if (!partner) {
            this.props.getPartner(id);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const id = props.match.params.id;
        const partnerPayload = props.partners[id];
        if (partnerPayload && !state.formData.id) {
            return { formData: partnerPayload };
        }
        return null;
    }

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateSnappayOptions = (): FormPickerDatas[] => {
        const { t } = this.props;
        const optionsSnappay: FormPickerDatas[] = [
            { id: 'activated', name: t('snappay_status.activated') },
            { id: 'available', name: t('snappay_status.available') },
            { id: 'unavailable', name: t('snappay_status.unavailable') },
            { id: 'revoked', name: t('snappay_status.revoked') },
        ];
        return optionsSnappay;
    };

    generateInputs = (): FormDatas[] => {
        const { t } = this.props;
        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    {
                        id: 'company_type',
                        label: t('partner_type'),
                        optionsGroups: (
                            <>
                                <optgroup label={t('partner_types.independent')}>
                                    <option value="partner_types.artisan">{t('partner_types.artisan')}</option>
                                </optgroup>
                                <optgroup label={t('partner_types.company')}>
                                    <option value="micro-entreprise">{t('partner_types.micro-enterprise')}</option>
                                    <option value="EI">{t('partner_types.ei')}</option>
                                    <option value="EIRL">{t('partner_types.eirl')}</option>
                                    <option value="EURL">{t('partner_types.eurl')}</option>
                                    <option value="SASU">{t('partner_types.sasu')}</option>
                                    <option value="SAS">{t('partner_types.sas')}</option>
                                    <option value="SARL">{t('partner_types.sarl')}</option>
                                    <option value="SA">{t('partner_types.sa')}</option>
                                    <option value="SNC">{t('partner_types.snc')}</option>
                                    <option value="SE">{t('partner_types.se')}</option>
                                </optgroup>
                            </>
                        ),
                    },
                    { id: 'share_capital', label: t('share_capital') },
                    { id: 'siret', label: t('siret') },
                    {
                        id: 'bank_iban',
                        placeholder: t('iban_placeholder'),
                        hint: t('iban_hint'),
                        inputsAttributs: { pattern: IBAN_INPUT_PATTERN, title: t('iban_hint') },
                        format: editIban,
                        label: t('iban'),
                    },
                    { id: 'bank_bic', label: t('bic') },
                    { id: 'vat_number', label: t('vat_number') },
                    { id: 'rcs', label: t('rcs') },
                    { id: 'licence_number_atout_france', label: t('vtc_number') },
                    {
                        id: 'express_payment_status',
                        label: t('snappay'),
                        options: this.generateSnappayOptions(),
                    },
                    {
                        id: 'additional_info',
                        type: 'textarea',
                        label: t('common:form.additional_infos'),
                    },
                ],
            },
            {
                title: t('address'),
                datas: [
                    {
                        id: 'billing_address[street]',
                        label: t('common:form.address.street'),
                    },
                    {
                        id: 'billing_address[postal_code]',
                        label: t('common:form.address.postal_code'),
                    },
                    { id: 'billing_address[city]', label: t('common:form.address.city') },
                    {
                        id: 'billing_address[country]',
                        label: t('common:form.address.country'),
                    },
                ],
            },
            // {
            //     title: t('billing'),
            //     datas: [
            //         {
            //             id: 'billing_period[days]',
            //             label: t('billing_components.day'),
            //             type: 'number',
            //         },
            //         {
            //             id: 'billing_period[weeks]',
            //             label: t('billing_components.week'),
            //             type: 'number',
            //         },
            //         {
            //             id: 'billing_period[months]',
            //             label: t('billing_components.month'),
            //             type: 'number',
            //         },
            //     ],
            // },
        ];
        return inputs;
    };

    onSubmitEdit = (formData: any, event): void => {
        const { match, editPartner } = this.props;
        const id = match.params.id;
        this.setState({
            formData: formData,
        });
        editPartner(id, { ...formData, bank_iban: normalizeIban(formData.bank_iban) });
    };

    render() {
        const { formData } = this.state;
        const { match, editPartnerError, isEditingPartner } = this.props;
        const id = match.params.id;

        return (
            <App needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={editPartnerError} />
                {!ObjectUtils.isEmpty(formData) && (
                    <>
                        <EnhancedForm
                            payload={formData}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.partners}/${id}`}
                            onSubmit={this.onSubmitEdit}
                            loader={isEditingPartner}
                        />
                    </>
                )}
            </App>
        );
    }
}

const PartnerEditPageWithTranslation = withTranslation(['partner', 'common'])(PartnerEditPage);

const mapStateToProps = (state) => ({
    partners: partnerSelectors.partners(state),
    editPartnerError: errorSelectors.editPartnerError(state),
    isEditingPartner: pendingSelectors.isPending(state, partnerTypes.EDIT_PARTNER),
});

const mapDispatchToProps = {
    getPartner: partnerActions.getPartner,
    editPartner: partnerActions.editPartner,
};

export default connect(mapStateToProps, mapDispatchToProps)(PartnerEditPageWithTranslation);
